/* eslint-disable canonical/filename-match-exported */
import {
  AuthenticationError,
  Backoff,
  Client,
  ConnectionError,
  MaintenanceError,
  UpgradeRequiredError,
} from '@openphone/internal-api-client'
import uuid from '@openphone/lib/uuid'
import { trackCustomAction } from '@openphone/quo-observability'
import Debug from 'debug'
import { action, makeObservable, observable, reaction } from 'mobx'
import type { Observable } from 'rxjs'
import { Subject } from 'rxjs'
import { filter, share } from 'rxjs/operators'

import config, { platform, deviceId } from '@src/config'
import { logError } from '@src/lib/log'
import type Service from '@src/service'
import PersistedCollection from '@src/service/collections/PersistedCollection'
import type { TransactionRepository } from '@src/service/worker/repository'

import AiClient from './AiClient'
import CopilotClient from './CopilotClient'
import MarketplaceClient from './MarketplaceClient'
import SearchClient from './SearchClient'
import TrustClient from './TrustClient'
import WorkflowClient from './WorkflowClient'
import AccountClient from './account'
import AuthClient from './auth'
import BillingClient from './billing'
import CommunicationClient from './communication'
import ContactsClient from './contacts'
import IntegrationClient from './integration'
import Connectivity from './network/connectivity'
import WebSocket from './network/websocket'
import redactEvent from './redactEvent'
import ReportClient from './report'
import SnippetsClient from './snippet'
import type { Transaction } from './transaction'
import { HttpTransaction, WebsocketTransaction } from './transaction'
import VoiceClient from './voice'
import WebhooksClient from './webhooks'
import type { WebSocketMessage } from './websocket'
import WorkspaceClient from './workspace'

import { isExperimentFromReferral } from '@odyssey/feature-flags/experimentFromReferral'
import { getRootLogger } from '@odyssey/logging/logger'

export default class Transport {
  readonly client: Client
  readonly connectivity: Connectivity
  readonly websocket: WebSocket

  private transactions: PersistedCollection<Transaction, TransactionRepository>
  private blockedQueues = new Set<string>()
  private backoffs = new Map<string, Backoff>()

  private get log() {
    return getRootLogger().getChild(['transport'])
  }

  private debug = Debug('op:transport')
  private webSocketEnabled = true

  online = true
  /**
   * set a limit to the number of retry attempts for a transaction
   * to avoid infinite loops and to prevent spamming the server
   */
  private maxRetryAttempts = 3

  readonly account: AccountClient
  readonly ai: AiClient
  readonly marketplace: MarketplaceClient
  readonly auth: AuthClient
  readonly billing: BillingClient
  readonly communication: CommunicationClient
  readonly contacts: ContactsClient
  readonly copilot: CopilotClient
  readonly integration: IntegrationClient
  readonly snippets: SnippetsClient
  readonly report: ReportClient
  readonly search: SearchClient
  readonly voice: VoiceClient
  readonly workflow: WorkflowClient
  readonly workspace: WorkspaceClient
  readonly webhooks: WebhooksClient
  readonly trust: TrustClient

  onMessage: Observable<WebSocketMessage>

  constructor(private root: Service) {
    this.transactions = new PersistedCollection({
      table: this.root.storage.table('transaction'),
      classConstructor: (json): Transaction => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation!
        if (json.type === 'websocket') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
          return new WebsocketTransaction(json)
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
          return new HttpTransaction(json)
        }
      },
      compare: (t1, t2) => t1.createdAt - t2.createdAt,
    })

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.transactions.performQuery((query) => query.all())

    this.websocket = new WebSocket(root)

    this.connectivity = new Connectivity(this.root.presence, this.websocket)
    const getAccessToken = () => this.root.authV2.getAccessToken()

    this.client = new Client({
      app: 'web',
      appVersion: config.VERSION,
      config,
      device: platform ?? 'browser',
      deviceId,
      getAccessToken,
      desktopVersion: this.root.desktopVersion,
      isExperimentFromReferral,
    })

    const onMessageSubject = new Subject<WebSocketMessage>()
    this.onMessage = onMessageSubject.asObservable().pipe(share())

    this.websocket.getAccessToken = getAccessToken

    this.websocket.on('open', this.handleSocketOpen)

    this.websocket.on('error', (error) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- FIXME: Fix this ESLint violation!
      this.log.debug(error.message, { error })
    })

    this.websocket.on('message', (data) => {
      if (this.webSocketEnabled) {
        onMessageSubject.next(data)
        this.logNotificationData(data)
      }
    })

    this.websocket.on('unauthorized', () => {
      this.debug('Received unauthorized. Refreshing the token...')
      this.handleReconnectWsClientWithNewToken().catch(logError)
    })

    this.websocket.on('accessDenied', () => {
      trackCustomAction({
        type: 'logging-out-user-websocket-access-denied',
      })

      root.clearAllAndRestart({ reason: 'websocket-access-denied' }).catch(logError)
    })

    this.account = new AccountClient(this)
    this.ai = new AiClient(this)
    this.marketplace = new MarketplaceClient(this)
    this.auth = new AuthClient(this)
    this.billing = new BillingClient(this)
    this.communication = new CommunicationClient(this)
    this.contacts = new ContactsClient(this)
    this.copilot = new CopilotClient(this)
    this.integration = new IntegrationClient(this)
    this.report = new ReportClient(this)
    this.snippets = new SnippetsClient(this)
    this.voice = new VoiceClient(this)
    this.workflow = new WorkflowClient(this)
    this.search = new SearchClient(this)
    this.workspace = new WorkspaceClient(this)
    this.webhooks = new WebhooksClient(this)
    this.trust = new TrustClient(this)

    makeObservable<
      this,
      | 'websocket'
      | 'transactions'
      | 'runTransaction'
      | 'runPendingTransactions'
      | 'resetTransaction'
      | 'runNextTransactionInQueue'
      | 'getBackoff'
    >(this, {
      websocket: observable,
      transactions: observable,
      online: observable,
      queue: action,
      runTransaction: action,
      runPendingTransactions: action,
      resetTransaction: action,
      runNextTransactionInQueue: action,
      getBackoff: action,
    })

    reaction(
      () => this.connectivity.online,
      (online) => {
        this.debug('went %s', online ? 'online' : 'offline')
        this.online = online
        if (online) {
          this.runPendingTransactions()
        }
      },
      { name: 'TransportOnline' },
    )
  }

  get onNotificationData() {
    return this.onMessage.pipe(
      filter(
        (m): m is Extract<WebSocketMessage, { _class: 'NotificationData' }> =>
          m._class === 'NotificationData',
      ),
    )
  }

  private logNotificationData(data: WebSocketMessage) {
    if (config.ENV === 'production') {
      return
    }

    const isDebugEnabled = this.log.isEnabledFor('debug')
    const isPlaywright = '__PLAYWRIGHT__' in window && window['__PLAYWRIGHT__']

    if (isDebugEnabled || isPlaywright) {
      const redacted = redactEvent(data)

      if (isDebugEnabled) {
        this.log.debug('socket event received: {event}', {
          event: JSON.stringify(redacted),
        })
      }

      if (isPlaywright) {
        trackCustomAction({ type: 'websocket-event', event: redacted })
      }
    }
  }

  /**
   * Queue the transaction to be run as soon as possible
   */
  queue<T>(trx: Transaction<T>): Promise<T> {
    if (!trx.skipPersisting) {
      this.transactions.put(trx)
    }
    this.runTransaction(trx)
    return trx.promise()
  }

  runPendingTransactions() {
    this.transactions.list.forEach((trx) => {
      if (trx.isPending) {
        this.runTransaction(trx)
      }
    })
  }

  /**
   * When token is refreshed, if websocket connection is closed, start a new one.
   * Otherwise, send the updated token over as a command.
   */
  handleRefreshAuthToken = (token: string) => {
    try {
      if (this.websocket.state === 'closed' && token) {
        return this.websocket.connect()
      }

      if (this.websocket.state === 'open' && token) {
        trackCustomAction({
          type: 'universal-login-refresh-websocket-token',
        })

        return this.websocket.send({
          _class: 'Command',
          path: '/refresh',
          params: { token: token },
        })
      }

      this.log.debug(
        `Websocket state: ${this.websocket.state}. Not able to update the connection.`,
      )
    } catch (error) {
      logError(error)
    }
  }

  private runTransaction(trx: Transaction) {
    /**
     * If the queue for this trx is blocked, just return. Once the
     * queue is opened, this will be picked up by this.runNextTransactionInQueue
     *
     * If the transaction has reached the maximum number of retry attempts,
     * abort the transaction.
     */
    if (
      (trx.queue && this.blockedQueues.has(trx.queue)) ||
      (trx.retryAttempts && trx.retryAttempts >= this.maxRetryAttempts)
    ) {
      return
    }

    let operation: Promise<any> | null = null

    if (trx instanceof HttpTransaction) {
      if (trx.retryAttempts === undefined) {
        trx.retryAttempts = 0
      }

      trx.retryAttempts += 1

      /**
       * If this is a retryable trx and network is down, leave it for later
       * when network is back up.
       */
      if (trx.retry && !this.online) {
        this.resetTransaction(trx, { discard: false })
        return
      }

      if (trx.queue) {
        this.blockedQueues.add(trx.queue)
      }

      /**
       * Run the transaction. If it fails and retry is not possible, fail with
       * the error.
       */
      operation = trx.run(this.client)
    } else if (trx instanceof WebsocketTransaction) {
      if (this.websocket.state !== 'open') {
        this.resetTransaction(trx, { discard: false })
        return
      }
      if (trx.queue) {
        this.blockedQueues.add(trx.queue)
      }
      operation = trx.run(this.websocket)
    }

    if (!operation) {
      return
    }

    operation
      .then(
        action(() => {
          this.resetTransaction(trx, { discard: true })
        }),
      )
      .catch(
        action(async (error) => {
          if (error instanceof UpgradeRequiredError) {
            trackCustomAction({
              type: 'logging-out-user-upgrade-required',
            })

            this.root.clearAllAndRestart({ reason: 'upgrade-required' }).catch(logError)
            return
          }

          // On 401, force-refresh the token and retry once.
          // If the retry also 401s, release('new-token-also-failed') triggers logout.
          if (error instanceof AuthenticationError && trx instanceof HttpTransaction) {
            const emit401 = (
              outcome: 'retry-succeeded' | 'retry-failed' | 'refresh-failed',
            ) => {
              trackCustomAction({
                type: 'http-401',
                url: safeUrlPath(trx.url),
                method: (trx.method ?? 'GET').toUpperCase(),
                outcome,
              })
            }

            try {
              const { token, release } = await this.root.authV2.forceRefreshAccessToken()

              if (!token) {
                // Force refresh failed (already logging out)
                emit401('refresh-failed')
                trx.reject(error)
                this.resetTransaction(trx, { discard: true })
                return
              }

              try {
                await trx.run(this.client)
                emit401('retry-succeeded')
                release('new-token-succeeded')
                this.resetTransaction(trx, { discard: true })
              } catch (retryError) {
                emit401('retry-failed')
                release(
                  retryError instanceof AuthenticationError
                    ? 'new-token-also-failed'
                    : 'new-token-succeeded',
                )
                trx.reject(retryError as Error)
                this.resetTransaction(trx, { discard: true })
              }
            } catch (refreshError) {
              emit401('refresh-failed')
              logError(refreshError)
              trx.reject(error)
              this.resetTransaction(trx, { discard: true })
            }
            return
          }

          if (
            trx.retry &&
            (error instanceof ConnectionError || error instanceof MaintenanceError)
          ) {
            const backoff = this.getBackoff(trx)
            try {
              backoff.backoff(error)
            } catch (error) {
              logError(error)
            }
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
            trx.reject(error)
            this.resetTransaction(trx, { discard: true })

            this.log.error(`Failed to execute transaction ${trx.id}`, { trx, error })
          }
        }),
      )
  }

  private runNextTransactionInQueue(trx: Transaction) {
    if (!trx.queue) {
      return
    }
    const next = this.transactions.list.find((t) => t.queue === trx.queue)
    if (next) {
      this.runTransaction(next)
    }
  }

  private resetTransaction(trx: Transaction, opts: { discard: boolean }) {
    if (trx.queue) {
      this.blockedQueues.delete(trx.queue)
    }

    this.backoffs.get(trx.id)?.reset()
    this.backoffs.delete(trx.id)

    if (opts.discard) {
      this.transactions.delete(trx)
      this.runNextTransactionInQueue(trx)
    }
  }

  private getBackoff(trx: Transaction): Backoff {
    const cachedBackoff = this.backoffs.get(trx.id)
    if (cachedBackoff) {
      return cachedBackoff
    }

    const backoff = Backoff.exponential({
      factor: 2.0,
      initialDelay: 100,
      maxDelay: 60000,
      randomisationFactor: 0.4,
    })

    backoff.on('backoff', (number, delay, error) => {
      this.log.debug(`Retry #${number} in ${delay}ms`, { trx, error })
    })

    backoff.on('ready', () => {
      this.runTransaction(trx)
    })

    backoff.on('fail', (error) => {
      trx.reject(error ?? new Error('Unknown error'))
      this.resetTransaction(trx, { discard: true })
      this.log.error(`Failed to execute transaction.`, { trx, error })
    })

    return backoff
  }

  private handleSocketOpen = () => {
    let uniqueId = window.localStorage.getItem('uid')
    if (!uniqueId) {
      uniqueId = uuid()
      window.localStorage.setItem('uid', uniqueId)
    }
    this.websocket.send({
      _class: 'Command',
      path: '/app',
      params: {
        uniqueId,
        platform: platform ?? 'browser',
        name: 'web',
        version: config.VERSION,
      },
    })
    this.websocket.send({
      path: '/push',
      params: {},
      _class: 'Observe',
    })
  }

  private async handleReconnectWsClientWithNewToken() {
    try {
      const { token, release } = await this.root.authV2.forceRefreshAccessToken()

      if (!token) {
        // Force refresh failed (already logging out)
        return
      }

      try {
        if (this.websocket.state === 'closed') {
          trackCustomAction({
            type: 'universal-login-reconnect-websocket',
          })

          await this.websocket.connect()
          release('new-token-succeeded')
          return
        }

        if (this.websocket.state === 'open') {
          trackCustomAction({
            type: 'universal-login-refresh-websocket-token',
          })

          this.websocket.send({
            _class: 'Command',
            path: '/refresh',
            params: { token },
          })
          release('new-token-succeeded')
          return
        }

        release('new-token-succeeded')
        trackCustomAction({
          type: 'universal-login-websocket-error',
          error: new Error(
            `Not able to update the Websocket connection. State ${this.websocket.state}.`,
          ),
        })
      } catch (error) {
        release('new-token-succeeded')
        throw error
      }
    } catch (error) {
      logError(error)
      this.debug('Failed to get access token for websocket reconnection')

      trackCustomAction({
        type: 'universal-login-websocket-error',
        error: error as Error,
      })
    }
  }
}

function safeUrlPath(url: string | null): string {
  if (!url) {
    return ''
  }
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}
